import { useCallback, useEffect, useRef } from 'react';
import { useGoogleAnalytics } from '@michelin/central-provider/dist/hooks/useGoogleAnalytics';
import { useUIVersion } from 'contexts/UIVersionContext';

export type ProfileType =
  | 'billing'
  | 'tire'
  | 'wheel'
  | 'mechanical'
  | 'contact'
  | 'account'
  | 'location'
  | 'vehicle'
  | 'sop';
export type ProfileFormAction = 'create' | 'edit' | 'clone';
type ProfileAction = ProfileFormAction | 'delete';
type ProfileInterface = 'new' | 'legacy';
type ExitType = 'cancel' | 'navigated_away' | 'closed_tab';

interface FormAttempt {
  action: ProfileFormAction;
  interface: ProfileInterface;
  saveError: boolean;
}

function toFormAction(mode: string | null | undefined): ProfileFormAction | null {
  const normalized = mode?.toLowerCase();
  if (normalized === 'add' || normalized === 'create') return 'create';
  if (normalized === 'edit') return 'edit';
  if (normalized === 'clone') return 'clone';
  return null;
}

function useSendEvent() {
  const googleAnalytics = useGoogleAnalytics();
  const googleAnalyticsRef = useRef(googleAnalytics);
  googleAnalyticsRef.current = googleAnalytics;

  return useCallback((eventName: string, params: Record<string, string>) => {
    googleAnalyticsRef.current?._gtag('event', eventName, params);
  }, []);
}

function useCurrentInterface() {
  const { isNewUI } = useUIVersion();
  const interfaceRef = useRef<ProfileInterface>(isNewUI ? 'new' : 'legacy');
  interfaceRef.current = isNewUI ? 'new' : 'legacy';
  return interfaceRef;
}

/** Tracks completed profile actions that have no form, such as deletes and inline edits. */
export function useProfileActionTracking() {
  const sendEvent = useSendEvent();
  const interfaceRef = useCurrentInterface();

  return useCallback(
    (action: ProfileAction, profileType: ProfileType) => {
      sendEvent('profile_action', { action, profile_type: profileType, interface: interfaceRef.current });
    },
    [sendEvent, interfaceRef],
  );
}

/** Leaving a create/edit/clone `mode` without calling `complete` sends `profile_form_abandoned`. */
export function useProfileFormTracking(profileType: ProfileType, mode: string | null | undefined) {
  const formAction = toFormAction(mode);
  const formActionRef = useRef(formAction);
  formActionRef.current = formAction;
  const sendEvent = useSendEvent();
  const interfaceRef = useCurrentInterface();
  const attemptRef = useRef<FormAttempt | null>(null);

  const abandon = useCallback(
    (exitType: ExitType) => {
      const attempt = attemptRef.current;
      if (!attempt) return;
      attemptRef.current = null;
      sendEvent('profile_form_abandoned', {
        action: attempt.action,
        profile_type: profileType,
        interface: attempt.interface,
        exit_type: exitType,
        save_error: String(attempt.saveError),
      });
    },
    [sendEvent, profileType],
  );

  useEffect(() => {
    if (!formAction) return undefined;

    attemptRef.current = { action: formAction, interface: interfaceRef.current, saveError: false };
    const onPageHide = () => abandon('closed_tab');
    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      abandon('navigated_away');
    };
  }, [formAction, abandon, interfaceRef]);

  const complete = useCallback(() => {
    const attempt = attemptRef.current;
    const action = attempt?.action ?? formActionRef.current;
    attemptRef.current = null;
    if (!action) return;
    sendEvent('profile_action', {
      action,
      profile_type: profileType,
      interface: attempt?.interface ?? interfaceRef.current,
    });
  }, [sendEvent, profileType, interfaceRef]);

  const cancel = useCallback(() => abandon('cancel'), [abandon]);

  const saveFailed = useCallback(() => {
    if (attemptRef.current) attemptRef.current.saveError = true;
  }, []);

  // For forms that stay open after a successful save to start another entry.
  const restart = useCallback(() => {
    const action = formActionRef.current;
    if (action) attemptRef.current = { action, interface: interfaceRef.current, saveError: false };
  }, [interfaceRef]);

  return { complete, cancel, saveFailed, restart };
}

export type ProfileFormTracking = ReturnType<typeof useProfileFormTracking>;
